import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { api_react_v1_poll_path } from '@routes';
import { stripHtml } from '@utils/formatting';

import request from './request';

dayjs.extend(customParseFormat);

export enum PollStatus {
  Draft = 'draft',
  Active = 'active',
  Closed = 'closed',
}

export type PollOption = {
  id: number;
  text: string;
  position: number;
  votes_count: number;
  percentage: number;
};

export type Poll = {
  id: number | null;
  question: string;
  status: 'draft' | 'active' | 'closed';
  expires_at?: string | null;
  poll_options: PollOption[];
  total_votes: number;
  user_voted: boolean;
  user_voted_option_id: number | null;
};

export type PollOptionAttributes = {
  id?: number;
  text: string;
  position: number;
  _destroy?: boolean;
};

export type PollAttributes = {
  question: string;
  expires_at: string;
  poll_options_attributes: PollOptionAttributes[];
};

export type CreateForumPostParams = {
  title: string;
  external_url?: string;
  body?: string;
  poll_attributes?: PollAttributes;
};

export const MIN_POLL_OPTIONS = 2;
export const MAX_POLL_OPTIONS = 4;
export const DEFAULT_POLL_EXPIRATION_DAYS = 3;
export const DEFAULT_POLL_EXPIRATION_HOUR = 23;
export const DEFAULT_POLL_EXPIRATION_MINUTE = 59;
export const MAX_POLL_EXPIRATION_DAYS = 7;
export const POLL_EXPIRES_AT_FORMAT = 'MMMM D, YYYY, h:mm A';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function toLocalDate(value?: string | Date | dayjs.Dayjs | null): Date | null {
  if (!value) return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  if (dayjs.isDayjs(value)) {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const parsed = dayjs(value, POLL_EXPIRES_AT_FORMAT, true);
  if (parsed.isValid()) return parsed.toDate();
  const paddedTime = dayjs(value, 'MMMM D, YYYY, hh:mm A', true);
  if (paddedTime.isValid()) return paddedTime.toDate();
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function formatLocalDateTime(date: Date): string {
  const hour = date.getHours();
  const hour12 = hour % 12 || 12;
  const minute = String(date.getMinutes()).padStart(2, '0');
  const ampm = hour >= 12 ? 'PM' : 'AM';
  return `${
    MONTH_NAMES[date.getMonth()]
  } ${date.getDate()}, ${date.getFullYear()}, ${hour12}:${minute} ${ampm}`;
}

export function atLocalEndOfDay(value: Date | string | dayjs.Dayjs = new Date()) {
  const date = toLocalDate(value) || new Date();
  date.setHours(DEFAULT_POLL_EXPIRATION_HOUR, DEFAULT_POLL_EXPIRATION_MINUTE, 0, 0);
  return dayjs(date);
}

export function defaultPollExpiresAt(from = new Date()): string {
  const date = new Date(from.getTime());
  date.setDate(date.getDate() + DEFAULT_POLL_EXPIRATION_DAYS);
  date.setHours(DEFAULT_POLL_EXPIRATION_HOUR, DEFAULT_POLL_EXPIRATION_MINUTE, 0, 0);
  return formatLocalDateTime(date);
}

export function emptyPollAttributes(): PollAttributes {
  return {
    question: '',
    expires_at: defaultPollExpiresAt(),
    poll_options_attributes: [
      { text: '', position: 0 },
      { text: '', position: 1 },
    ],
  };
}

export function parsePollExpiresAt(value?: string | Date | null): dayjs.Dayjs | null {
  const date = toLocalDate(value);
  return date ? dayjs(date) : null;
}

export function formatPollExpiresAt(value?: string | Date | null): string {
  const date = toLocalDate(value);
  if (!date) return '';
  return formatLocalDateTime(date);
}

export function withLocalDefaultExpirationTime(value?: string | Date | null): string {
  const date = parsePollExpiresAt(value);
  if (!date) return defaultPollExpiresAt();
  return formatPollExpiresAt(atLocalEndOfDay(date).toDate());
}

export function pollExpirationBounds(from = new Date()): { minDate: Date; maxDate: Date } {
  return {
    minDate: from,
    maxDate: dayjs(from).add(MAX_POLL_EXPIRATION_DAYS, 'day').toDate(),
  };
}

export function isPollExpirationInAllowedWindow(
  value?: string | Date | null,
  from = new Date()
): boolean {
  if (!value) return false;
  const date = parsePollExpiresAt(value);
  if (!date) return false;
  const { minDate, maxDate } = pollExpirationBounds(from);
  return (
    date.isAfter(dayjs(minDate).subtract(1, 'minute')) &&
    date.isBefore(dayjs(maxDate).add(1, 'minute'))
  );
}

export function isPollExpirationPast(expiresAt?: string | Date | null, from = new Date()): boolean {
  const date = parsePollExpiresAt(expiresAt);
  return !!date && date.isBefore(dayjs(from));
}

export function isClosedPoll(
  poll?: Pick<Poll, 'status' | 'expires_at'> | null,
  from = new Date()
): boolean {
  if (!poll) return false;
  return poll.status === PollStatus.Closed || isPollExpirationPast(poll.expires_at, from);
}

export function pollDaysRemaining(expiresAt?: string | Date | null, from = new Date()): number {
  const date = parsePollExpiresAt(expiresAt);
  if (!date) return 0;
  return date.diff(dayjs(from), 'day');
}

export function pollToAttributes(poll: Poll): PollAttributes {
  return {
    question: poll.question,
    expires_at: formatPollExpiresAt(poll.expires_at),
    poll_options_attributes: poll.poll_options.map((option) => ({
      id: option.id,
      text: option.text,
      position: option.position,
    })),
  };
}

export function isStandalonePollPost(post: {
  poll?: Poll | null;
  title?: string;
  comment?: { body?: string; snippet?: string } | null;
}): boolean {
  if (!post.poll) return false;

  const question = post.poll.question.trim();
  const title = (post.title || '').trim();
  if (title && title !== question) return false;

  const bodyText = stripHtml(post.comment?.body || post.comment?.snippet || '').trim();
  return !bodyText || bodyText === question;
}

export function canRemovePoll(poll?: Poll | null): boolean {
  return !!poll;
}

export function canEditPoll(poll?: Poll | null, { isStaff }: { isStaff?: boolean } = {}): boolean {
  if (!poll) return false;
  if (isStaff) return true;
  return poll.total_votes === 0;
}

export async function fetchPoll(pollId: number | string): Promise<Poll> {
  const resp = await request('get', api_react_v1_poll_path(pollId));
  return resp;
}

export async function castStandalonePollVote({
  pollId,
  pollOptionId,
}: {
  pollId: number | string;
  pollOptionId: number;
}): Promise<Poll> {
  const resp = await request('post', `${api_react_v1_poll_path(pollId)}/vote`, {
    poll_option_id: pollOptionId,
  });
  return resp;
}
